import { getDocumentPreview } from '@app/shared/document-preview/api'

import type { Attachment } from '../types'
import type { DocumentPreviewResponse } from '@app/shared/document-preview/types'
import type { AttachingMode } from '../constants'
import type { LiquidDocument } from '@app/shared/types/sf-data'
import type { FileWithProgress } from '@app/shared/vue/components/direct-upload/types'

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { attachingMode as attachingModeConstant } from '../constants'
import { isExport, isExternalLink } from '../predicates'
import { DocumentLinkType } from '@app/shared/types/sf-data'

export const useSidebarFileAttachmentsStore = defineStore(
  'sidebarFileAttachments',
  () => {
    // State
    const attachments = ref<Attachment[]>([])
    const attachingMode = ref<AttachingMode>(attachingModeConstant.NEW_FILES)
    const changesAreDisabled = ref(false)
    const currentExternalLinkId = ref(0)
    const errorMessage = ref<string>('')
    const autoLinkedAttachmentKeys = ref<Set<string>>(new Set())
    const previewExpanded = ref<{
      currentDocumentId: number | null
      open: boolean
    }>({
      currentDocumentId: 0,
      open: false,
    })
    const documentPreviewPerDocumentId = ref<
      Record<number, DocumentPreviewResponse | null>
    >({})

    // Getters
    const attachmentsCount = computed(
      () => attachments.value.filter((a) => !a.uploadId).length
    )

    const currentExternalLink = computed(() =>
      attachments.value.find(
        (attachment) => attachment.document_id === currentExternalLinkId.value
      )
    )

    const hasOnePreviewableDocument = computed(
      () => attachments.value.length === 1 && attachments.value[0].previewable
    )

    // Actions
    function loadAttachments(liquidDocuments: LiquidDocument[]): void {
      attachingMode.value = attachingModeConstant.NEW_FILES
      errorMessage.value = ''

      attachments.value = prepareForStorage(liquidDocuments)
      changesAreDisabled.value = false
    }

    function clearExternalLink(): void {
      currentExternalLinkId.value = 0
    }

    function editExternalLink(linkId: number): void {
      changeMode(attachingModeConstant.EXTERNAL_LINK)
      currentExternalLinkId.value = linkId
    }

    function handleFileAdded(file: FileWithProgress, uuid: string): void {
      const newAttachment = {
        document_id: (attachments.value.length + 1) * -1,
        uploadId: uuid,
        name: file.file?.name || 'Uploading...',
        size: file.file?.size || 0,
        linkType: DocumentLinkType.DirectUpload,
        previewable: false,
      }

      attachments.value.push(newAttachment)
    }

    function handleFileUploaded(file: FileWithProgress, uuid: string): void {
      const newAttachment = attachments.value.find(
        (attachment) => attachment.uploadId === uuid
      )

      // Attachment upload got cancelled?
      if (newAttachment === undefined) return

      newAttachment.link = file.blob?.signed_id
    }

    function handleFileUploadProgress(
      uuid: string,
      file: FileWithProgress,
      progress: string
    ): void {
      const newAttachment = attachments.value.find(
        (attachment) => attachment.uploadId === uuid
      )

      // Attachment upload got cancelled?
      if (newAttachment === undefined) return

      newAttachment.uploadProgress = progress
    }

    // This only prevents new uploads being linked, we don't cancel the XHRs because
    // it won't clear up everything e.g. the database
    function cancelActiveUploads(): void {
      attachments.value = attachments.value.filter(
        (attachment) => !attachment.uploadId
      )
      changesAreDisabled.value = false
    }

    function prepareForStorage(freshDocuments: LiquidDocument[]): Attachment[] {
      return freshDocuments.map((doc) => {
        return {
          ...doc,
          size: isExternalLink(doc) ? 0 : doc.size ?? 0,
          previewable: isExternalLink(doc) ? false : doc.previewable ?? false,
          export_id: isExport(doc) ? doc.document_id : undefined,
        }
      })
    }

    function changeMode(mode: AttachingMode): void {
      attachingMode.value = mode
    }

    function toggleChangesAreDisabled(): void {
      changesAreDisabled.value = !changesAreDisabled.value
    }

    function markAsAutoLinked(key: string): void {
      autoLinkedAttachmentKeys.value.add(key)
    }

    function clearAutoLinkedAttachments(): void {
      autoLinkedAttachmentKeys.value = new Set()
    }

    async function fetchDocumentPreview(documentId: number): Promise<void> {
      if (documentPreviewPerDocumentId.value[documentId]) {
        return
      }

      const response = await getDocumentPreview(documentId)
      if (response) {
        documentPreviewPerDocumentId.value[documentId] = response
      }
    }

    function togglePreview(documentId: number): void {
      if (previewExpanded.value.currentDocumentId !== documentId) {
        openPreview(documentId)
      } else {
        previewExpanded.value.open = !previewExpanded.value.open
      }
    }

    function openPreview(documentId: number): void {
      previewExpanded.value.currentDocumentId = documentId
      previewExpanded.value.open = true
    }

    function raiseError(message: string): void {
      errorMessage.value = message
    }

    function closeExistingFilesBrowser(): void {
      previewExpanded.value = {
        currentDocumentId: 0,
        open: false,
      }
    }

    function clearError(): void {
      errorMessage.value = ''
    }

    return {
      // State
      attachments,
      attachingMode,
      autoLinkedAttachmentKeys,
      changesAreDisabled,
      errorMessage,
      previewExpanded,
      documentPreviewPerDocumentId,

      // Getters
      attachmentsCount,
      currentExternalLink,
      hasOnePreviewableDocument,

      // Actions
      cancelActiveUploads,
      changeMode,
      clearAutoLinkedAttachments,
      clearError,
      clearExternalLink,
      closeExistingFilesBrowser,
      markAsAutoLinked,
      editExternalLink,
      handleFileAdded,
      handleFileUploaded,
      handleFileUploadProgress,
      loadAttachments,
      prepareForStorage,
      togglePreview,
      openPreview,
      fetchDocumentPreview,
      toggleChangesAreDisabled,
      raiseError,
    }
  }
)
